import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/dah8ne3p14a1h3mr6fdg";
const MAX_LENGTH = 500;

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [length, setLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const guard = String(formData.get("website_alt") ?? "").trim();
    if (guard) {
      setStatus("success");
      setFormError("");
      return;
    }

    setStatus("submitting");
    setFormError("");

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key === "website_alt") return;
        const text = String(value);
        if (text.trim() === "") return;
        params.append(key, text);
      });

      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = responseText ? (JSON.parse(responseText) as FormResponse) : null;
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交没有成功，请稍后重试，或直接发送邮件联系我们。",
        );
        return;
      }

      setStatus("success");
      setFormError("");
      setLength(0);
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请稍后重试。");
    }
  };

  return (
    <form
      id="baoliao-submit-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-2xl border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="submit-name" className="text-sm font-medium text-foreground-800">
            称呼
          </label>
          <input
            id="submit-name"
            name="name"
            type="text"
            required
            placeholder="怎么称呼你都可以"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div>
          <label htmlFor="submit-email" className="text-sm font-medium text-foreground-800">
            联系邮箱
          </label>
          <input
            id="submit-email"
            name="email"
            type="email"
            required
            placeholder="用于补充核实，不会公开"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="submit-topic" className="text-sm font-medium text-foreground-800">
          投稿类型
        </label>
        <select
          id="submit-topic"
          name="topic"
          defaultValue="内幕爆料"
          className="mt-2 w-full rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        >
          <option value="内幕爆料">内幕爆料</option>
          <option value="娱乐八卦">娱乐八卦</option>
          <option value="职场内幕">职场内幕</option>
          <option value="科技数码">科技数码</option>
          <option value="生活百态">生活百态</option>
          <option value="情感树洞">情感树洞</option>
          <option value="内容纠错">内容纠错 / 举报</option>
          <option value="合作咨询">合作咨询</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="submit-content" className="text-sm font-medium text-foreground-800">
          具体内容
        </label>
        <textarea
          id="submit-content"
          name="content"
          rows={5}
          required
          maxLength={MAX_LENGTH}
          onChange={(e) => setLength(e.target.value.length)}
          placeholder="请尽量写清时间、地点、涉及方与可佐证的线索。支持匿名发布。"
          className="mt-2 w-full resize-none rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        />
        <p className={`mt-2 text-xs ${length > MAX_LENGTH ? "text-primary-600" : "text-foreground-500"}`}>
          已输入 {length}/{MAX_LENGTH} 字
        </p>
      </div>

      <div className="field-aux" aria-hidden="true">
        <label htmlFor="submit-website-alt">备用网址</label>
        <input
          id="submit-website-alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} aria-hidden="true" />
          {status === "submitting" ? "正在提交…" : "提交投稿"}
        </button>
        <p className="text-xs leading-relaxed text-foreground-500">
          提交即表示你同意我们基于内容规范进行整理与发布。
        </p>
      </div>

      {status === "success" && (
        <p
          role="status"
          className="mt-5 flex items-start gap-2 rounded-lg border border-secondary-200 bg-secondary-50 px-4 py-3 text-sm text-secondary-900"
        >
          <i className="ri-checkbox-circle-line mt-0.5 shrink-0" aria-hidden="true" />
          已收到你的投稿，编辑会在 1-2 个工作日内通过邮箱与你确认细节。感谢提供线索。
        </p>
      )}
      {status === "error" && formError && (
        <p role="alert" className="mt-5 flex items-start gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line mt-0.5 shrink-0" aria-hidden="true" />
          {formError}
        </p>
      )}
    </form>
  );
}